<template>
    <div id="player-container" :disabled="!active">
        <button id="play-pause-btn" class="btn" @click.prevent="togglePlayPause" :disabled="!active">
            <i v-if="!isPlaying" class="fa-solid fa-play fa-lg" title="Play"></i>
            <i v-else class="fa-solid fa-pause fa-lg" title="Pause"></i>
        </button>
    
        <div id="progress-container">
            <input type="range" id="progress" class="w-100 my-2" value="0" :max="duration" v-model.number="currentTime" :disabled="!active" @mousedown="pause" @change="seek" />
            <span id="progress-text">
                <Timer :elapsed="currentTime" />&nbsp;/&nbsp;<Timer :elapsed="duration" />
            </span>
        </div>

        <div id="volume-container">
            <i :class="`fa-solid fa-volume-${isMuted ? 'mute' : 'up'} fa-lg mx-2`" @click="toggleMute"></i>
            <input id="volume" class="w-100" type="range" min="0" max="1" step="0.01" value="0.5" @input="updateVolume" :disabled="!active" ref="volumeInput">
        </div>
    </div>
</template>

<script>
import Timer from '../Utilities/Timer.vue';

export default {
    components: { Timer },
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
        source: Object,
    },
    data() {
        return {
            isPlaying: false,
            isMuted: false,
            audioPlayer: null,
            currentTime: 0,
            duration: 0
        };
    },
    computed: {
        formattedCurrentTime() {
            return this.formatTime(this.currentTime);
        },
        formattedDuration() {
            return this.formatTime(this.duration);
        },
        active() {
            if (!this.disabled && this.source && this.source.url)
                return true;
            return false;
        }
    },
    watch: {
        source: {
            handler(newSource) {
                this.reset();

                if(!newSource || !newSource.url || newSource.url == "") {
                    if(this.audioPlayer) this.audioPlayer.src = ""
                    this.currentTime = 0
                    this.duration = 0
                    return
                }

                if(!this.audioPlayer) {
                    this.audioPlayer = new Audio()
                }

                this.audioPlayer.src = newSource.url
                this.audioPlayer.ontimeupdate = this.updateProgress;
                this.audioPlayer.onloadeddata = (e) => {
                    this.duration = isFinite(e.target.duration) ? e.target.duration : isFinite(newSource.duration) ? newSource.duration : this.duration;
                }
                this.audioPlayer.onended = () => {
                    this.reset();
                };
            },
            immediate: true
        }
    },
    mounted() {
        this.volumeInput = this.$refs.volumeInput;
    },
    beforeDestroy() {
        if(this.audioPlayer) this.audioPlayer.src = ""
    },
    methods: {
        formatTime(seconds) {
            if (isNaN(seconds) || seconds <= 0) {
                return '00:00';
            }
            const date = new Date(null);
            date.setSeconds(seconds);
            return `${date.getUTCMinutes().toString().padStart(2, '0')}:${date.getUTCSeconds().toString().padStart(2, '0')}`;
        },
        togglePlayPause() {
            if (!this.active || !this.source || !this.source.url)
                return;

            if (!this.isPlaying) {
                this.audioPlayer.play();
                this.isPlaying = true;
            }
            else {
                this.audioPlayer.pause();
                this.isPlaying = false;
            }
        },
        toggleMute() {
            if(!this.isMuted) {
                this.audioPlayer.volume = 0;
                this.isMuted = true
            } else {
                this.audioPlayer.volume = this.volumeInput.value;
                this.isMuted = false
            }
        },
        updateVolume(e) {
            this.audioPlayer.volume = e.target.value;
            this.isMuted = false
        },
        pause() {
            this.audioPlayer.pause();
        },
        seek(e) {
            this.currentTime = Number(e.target.value)
            this.audioPlayer.currentTime = this.currentTime
            if(this.isPlaying) this.audioPlayer.play()
        },
        updateProgress() {
            this.duration = isFinite(this.audioPlayer.duration) ? this.audioPlayer.duration : this.duration;
            this.currentTime = this.audioPlayer.currentTime;
        },
        reset() {
            this.audioPlayer && this.audioPlayer.load();
            this.currentTime = 0;
            this.isPlaying = false;
        }
    },
}
</script>

<style scoped>
#player-container {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    min-height: 2.3rem;
    width: 100%;
}
#player-container, 
#player-container i {
    color: white;
}

#player-container[disabled], 
#player-container[disabled] i {
    color: rgba(248, 248, 248, 0.6);
    cursor: not-allowed;
}

#player-container #volume-container,
#player-container #progress-container {
    display: flex;
    align-items: center;
    justify-content: space-around;
}

#player-container #volume-container {
    display: flex;
    transition: width 500ms cubic-bezier(0.19, 1, 0.22, 1);
    width: 40%;
}

#player-container:not([disabled]) #volume-container:hover{
    width: 100%;
    cursor: pointer;
}


#progress-container {
    flex-basis: 90%;
    position: relative;
}

#progress-container #progress {
    cursor: pointer;
    z-index: 1;
}

#progress-container > #progress-text  {
    position: absolute;
    user-select: none;
    right: 0.5rem;
    top: 1rem;
    white-space: nowrap;
    z-index: 0;
}
#progress-container > #progress-text > * {
    display: inline;
    font-size: small;
}
</style>